import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../shared/services/api.service';
import { LoaderService } from '../../shared/services/loader.service';
import { AuthService } from '../../shared/services/auth.service';
import { BehaviourSubjectService } from '../../shared/services/behaviour-subject.service';
import { NotificationService } from '../../shared/services/notification.service';

@Component({
  selector: 'app-faq',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './faq.component.html',
  styleUrl: './faq.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FaqComponent implements OnInit, OnDestroy {
  private pendingNavigationUrl: string | null = null;
  private destroy$ = new Subject<void>();
  private dataLoaded = false;

  faqItems: any[] = [];

  constructor(
    private apiService: ApiService,
    private loaderService: LoaderService,
    private authService: AuthService,
    private behaviourSubject: BehaviourSubjectService,
    private router: Router,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.dataLoaded) return;
    this.dataLoaded = true;

    this.getFaqlist();

    this.authService.isLoggedIn$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoggedIn) => {
        if (isLoggedIn && this.pendingNavigationUrl) {
          this.pendingNavigationUrl = null;
          this.navigateToProfileFaq();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleExpanded(index: number): void {
    this.faqItems[index].expanded = !this.faqItems[index].expanded;
    this.cdr.markForCheck();
  }

  getFaqlist(): void {
    const payload = { action_type: 'PRIORITY', limit: 5, offset: 0 };
    this.loaderService.show();

    this.apiService
      .faqList(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loaderService.hide();
          if (res.success && res.data) {
            this.faqItems = res.data;
            this.cdr.markForCheck();
          } else {
            console.warn('⚠️ FAQ API returned success: false');
            this.faqItems = [];
            // this.notificationService.info('No FAQs available at the moment');
            this.cdr.markForCheck();
          }
        },
        error: (err) => {
          this.loaderService.hide();
          console.error('❌ Failed to fetch FAQ list', err);

          // Handle specific error codes with user-friendly messages
          // if (err.status === 412) {
          //   this.notificationService.error(
          //     'Service temporarily unavailable. Please try again later.',
          //   );
          // } else if (err.status === 401) {
          //   this.notificationService.error(
          //     'Authentication required. Please log in.',
          //   );
          // } else if (err.status === 403) {
          //   this.notificationService.error(
          //     'Access denied. You do not have permission to view FAQs.',
          //   );
          // } else if (err.status === 404) {
          //   this.notificationService.info('FAQs not found.');
          // } else if (err.status === 500) {
          //   this.notificationService.error(
          //     'Server error. Please try again later.',
          //   );
          // } else {
          //   this.notificationService.error(
          //     err?.error?.data?.msg || 'Failed to load FAQs. Please try again.',
          //   );
          // }

          this.faqItems = [];
          this.cdr.markForCheck();
        },
      });
  }

  handleViewAllFaqs(event?: Event): void {
    event?.preventDefault();

    if (this.authService.isLoggedIn) {
      this.navigateToProfileFaq();
      return;
    }

    this.pendingNavigationUrl = '/user-profile?section=profile-faq';
    this.behaviourSubject.openLoginModal('other');
  }

  private navigateToProfileFaq(): void {
    this.router.navigate(['/user-profile'], {
      queryParams: { section: 'profile-faq' },
    });
  }

  /**
   * TrackBy function for FAQ items to improve rendering performance
   */
  trackByFaq(index: number, faq: any): any {
    return faq.id || faq.question || index;
  }
}
